<template>
  <div
    class="accordion-item"
    :class="[{ 'accordion-item--open': isOpen }, `accordion-item--${variant}`]"
    :data-id="id"
    v-data-test-id="'accordion-item'"
    @click="handleClick"
  >
    <div class="accordion-item__inner" v-data-test-id="'accordion-item-inner'">
      <div
        class="accordion-item__label"
        :class="{ 'accordion-item__label--link': isLink }"
        v-data-test-id="'accordion-item-label'"
        v-on="!isLink ? { click: toggleOpen } : {}"
      >
        {{ this.label }}

        <span
          v-if="typeof isActiveIndicatorVisible === 'boolean'"
          class="accordion-item__active-indicator"
          :class="{ 'accordion-item__active-indicator--visible': isActiveIndicatorVisible }"
        ></span>

        <slot v-if="$slots.icon" name="icon" />
        <div v-else class="accordion-item__cross">
          <div class="accordion-item__cross-element"></div>
          <div class="accordion-item__cross-element accordion-item__cross-element--vertical"></div>
        </div>
      </div>
      <Expandable
        ref="expandable"
        :isOpen="isOpen"
        :transitionTime="transitionTime"
        v-data-test-id="'accordion-item-content'"
        v-on="contentWrapperEventListeners"
      >
        <div class="accordion-item__content"><slot /></div>
      </Expandable>
    </div>
  </div>
</template>

<script>
import { scrollSmoothlyToElement } from '@/helpers/scroll'
import Expandable from '@/components/_shared/Expandable.vue'

export const VARIANTS = {
  large: 'large',
  small: 'small',
  boxed: 'boxed',
}

export default {
  name: 'AccordionItem',
  components: {
    Expandable,
  },

  props: {
    id: {
      type: String,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    label: {
      type: String,
      required: true,
    },

    link: {
      type: String,
      default: null,
    },

    isInitiallyOpen: {
      type: Boolean,
    },

    isTrackingClickEvent: {
      type: Boolean,
      default: true,
    },

    isClosingOnClickOfContent: {
      type: Boolean,
      default: false,
    },

    isActiveIndicatorVisible: {
      type: Boolean,
      default: undefined,
    },

    transitionTime: {
      type: Number,
    },

    variant: {
      type: String,
      default: VARIANTS.large,
      validator(value) {
        return Object.values(VARIANTS).includes(value)
      },
    },
  },

  data() {
    return {
      isOpen: this.isInitiallyOpen,
    }
  },

  computed: {
    isLink() {
      return this.link !== null
    },

    contentWrapperEventListeners() {
      return {
        'height-change': $event => this.$emit('height-change', $event),
        'height-change-end': () => this.$emit('height-change-end'),
        ...(this.isClosingOnClickOfContent && !this.isLink ? { click: this.toggleOpen } : {}),
      }
    },
  },

  watch: {
    isInitiallyOpen: {
      immediate: true,
      handler() {
        this.isOpen = this.isInitiallyOpen
      },
    },
  },

  created() {
    this.$root.$on('close-accordion-items', this.checkClose)
  },

  beforeDestroy() {
    this.$root.$off('close-accordion-items', this.checkClose)
  },

  methods: {
    handleClick() {
      if (this.isTrackingClickEvent && (this.isOpen || this.isLink)) {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.navigation,
          details: { action: 'accordion', label: this.id },
        })
      }
      if (this.isLink) this.scrollToLink()
    },

    toggleOpen() {
      this.isOpen = !this.isOpen
      this.$root.$emit('accordion-click', this.index)
    },

    checkClose(openIndex) {
      if (openIndex !== this.index) this.isOpen = false
    },

    scrollToLink() {
      scrollSmoothlyToElement(document.querySelector(this.link))
    },

    updateHeight() {
      this.$refs.expandable.updateHeight()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './AccordionItem';
</style>
